import { Directive, Injectable, Input } from '@angular/core';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { Capacitor } from '@capacitor/core';
import { ToastController } from '@ionic/angular';

import { DConfigCompras, IConfigComprasIcons } from '../../private/shared/models/historico-ventas';
import { GTMEventsCrash } from '../enums/Crashlytics';
import { CrashlyticsService } from './crashlytics/crashlytics.service';
import { DeviceService } from './device/device.service';

declare var cordova: any

@Directive()
@Injectable({
  providedIn: 'root'
})
export class TicketEntregaService {

  @Input() ticket = ''
  @Input() icono = 'assets/icon/ruta-truck-tradicional.svg'
  @Input() label = 'Ticket'
  @Input() fecha = ''
  @Input() color = 'tpplus'
  @Input() titulo = 'Ticket de Entrega'
  @Input() configCompras: IConfigComprasIcons = DConfigCompras

  public toast: HTMLIonToastElement = {} as HTMLIonToastElement;

  constructor(
    private file: File
    , private socialSharing: SocialSharing
    , private crashlytics: CrashlyticsService
    , private toastController: ToastController
    , private androidPermissions: AndroidPermissions
    , private deviceS: DeviceService
  ) { }

  public onDownload = async (html: string, name: string) => {

    const fileName = `${name}.pdf`.replace(/ /g, '-');
    const contentType = 'application/pdf'
    const options = {
      documentSize: 'A7',
      type: 'base64'
    };

    const base64: string = await cordova.plugins.pdf.fromData(html, options);
    const androidVersion = await this.deviceS.getOSMajorVersion();

    if (Capacitor.getPlatform() === 'android' && androidVersion < 13) {
      const { hasPermission } = await this.androidPermissions.checkPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE);
      if (!hasPermission) {
        const result = await this.androidPermissions.requestPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE);
        if (!result.hasPermission) {
          return await this.presentToast('Se requiere el permiso para almacenar el ticket', 'alert');
        } else {
          return await this.sharing(fileName, base64, contentType);
        }
      } else {
        return await this.sharing(fileName, base64, contentType);
      }
    } else {
      return await this.sharing(fileName, base64, contentType);
    }
  }

  private sharing = async (fileName: string, b64Data: string, contentType: string) => {
    const folderpath = this.file.cacheDirectory
    const resultado = await this.savebase64AsPDF(folderpath, fileName, b64Data, contentType)

    return await this.socialSharing.share(
      folderpath
      , folderpath
      , resultado
    )
  }

  /**
   * Convierta una cadena base64 en un Blob de acuerdo con los datos y contentType.
   * @param b64Data Cadena pura base64 sin contentType
   * @param contentType el tipo de contenido del archivo, es decir, (application/pdf - text/plain)
   * @param sliceSize SliceSize para procesar el byteCharacters
   * @see http://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
   * @return Blob
   */
  private b64toBlob = (b64Data: string, contentType: string, sliceSize?: number) => {
    contentType = contentType || 'application/pdf'
    sliceSize = sliceSize || 512

    const byteCharacters = atob(b64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)

      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return blob
  }

  /**
   * Cree un archivo PDF de acuerdo con el contenido de su base de datos64 únicamente.
   * @param folderpath La carpeta donde se creará el archivo.
   * @param filename El nombre del archivo que se creará
   * @param content Importante: el contenido no puede contener la siguiente cadena (data:application/pdf;base64). Solo se espera la cadena base64.
   */
  private savebase64AsPDF = async (folderpath: string, filename: string, b64Data: string, contentType: string) => {
    // Convierta la cadena base64 en un Blob
    const DataBlob = this.b64toBlob(b64Data, contentType)

    try {
      // Se crea el archivo
      await this.file.createFile(folderpath, filename, true)
      // Se escribe el archivo
      await this.file.writeFile(folderpath, filename, DataBlob, { replace: true, append: false })

      return folderpath + filename
    } catch (error) {
      await this.presentToast(error['message'], 'alert')
      await this.crashlytics.sendCrashPlatform(GTMEventsCrash.filePdfPlugin, error);
      return error['message']
    }

  }

  public async presentToast(messageText: string, icon1: string) {

    this.toast = await this.toastController.create({
      message: messageText || '',
      position: 'bottom',
      duration: 7000,
      mode: 'ios',
      color: 'grayDemon',
      cssClass: '',
      buttons: [
        {
          side: 'start',
          icon: icon1,
          text: '',
          handler: () => {
            return false
          }
        },
        {
          icon: 'close',
          text: '',
          role: 'cancel',
          side: 'end',
          handler: () => { }
        }
      ]
    })

    return await this.toast.present()
  }

  public isDownload = () => {
    if (Capacitor.getPlatform() === 'android') {
      return 'android'
    } else if (Capacitor.getPlatform() === 'ios') {
      return 'ios'
    }
  }

}
